import { useEffect } from "react";
import { useUiProperty } from "@/features/UiState.slice.ts";

/**
 * Applies the persisted dark-mode preference before React mounts, so the first
 * paint isn't a light flash for dark-mode users.
 *
 * Reads the redux-persist blob straight out of localStorage: the store has not
 * rehydrated yet at this point, and waiting for it would put the flash back.
 * Values in there are Json strings, hence the `=== "true"`.
 */
export function applyInitialDarkMode() {
    const uiState = localStorage.getItem("persist:uiState");
    const uiStateParsed = uiState ? JSON.parse(uiState) : null;

    const initialDarkMode =
        uiStateParsed && typeof uiStateParsed.darkMode !== "undefined"
            ? uiStateParsed.darkMode === "true"
            : window.matchMedia("(prefers-color-scheme: dark)").matches;

    if (initialDarkMode) {
        document.documentElement.classList.add("dark");
    }
}

/**
 * Keeps the `dark` class on `<html>` in step with the persisted preference,
 * defaulting a fresh user to their Os setting.
 *
 * Every root that mounts a `DarkModeToggle` must call this: Tailwind's
 * `darkMode: "selector"` reads the class from the document root, so a toggle
 * whose root does not sync it flips the stored value and changes nothing on
 * screen. Wrapping a subtree in `<div className="dark">` is not a substitute —
 * headless-ui portals its menus to `<body>`, outside any wrapper, and the
 * class `applyInitialDarkMode` put on `<html>` keeps matching descendants
 * after the wrapper drops its own.
 */
export function useSyncDarkMode(): boolean | undefined {
    const [darkMode, setDarkMode] = useUiProperty<boolean>("darkMode");

    useEffect(() => {
        if (typeof darkMode === "undefined") {
            setDarkMode(
                window.matchMedia("(prefers-color-scheme: dark)").matches,
            );
        }
    }, [darkMode, setDarkMode]);

    useEffect(() => {
        // Undefined means "not decided yet": leave whatever
        // applyInitialDarkMode set, rather than flashing light for a frame.
        if (typeof darkMode === "undefined") {
            return;
        }

        document.documentElement.classList.toggle("dark", darkMode);
    }, [darkMode]);

    return darkMode;
}

/**
 * Keeps a signed-out screen light while it is mounted. Those screens are
 * drawn in the brand's own colours and have no dark version, but
 * `applyInitialDarkMode` has already put `dark` on `<html>` for a visitor
 * whose system prefers it. A screen there that uses the app's components
 * would get their dark text on its white page. The class is put back on
 * unmount, for the app the visitor signs in to.
 */
export function useLightSurface(): void {
    useEffect(() => {
        const root = document.documentElement;
        const wasDark = root.classList.contains("dark");
        root.classList.remove("dark");

        return () => {
            root.classList.toggle("dark", wasDark);
        };
    }, []);
}
